import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { 
  LayoutDashboard, 
  GitBranch, 
  Brain,
  Bell,
  Search,
  User,
  Zap
} from 'lucide-react';

import { EnhancedOperationsDashboard } from './pages/Dashboard/EnhancedOperationsDashboard';
import { WorkflowStudioHome } from './pages/WorkflowStudio/WorkflowStudioHome';
import { CompactWorkflowDetailView } from './pages/WorkflowStudio/CompactWorkflowDetailView';
import { AgentOrchestrator } from './pages/AgentOrchestrator/AgentOrchestrator';

const queryClient = new QueryClient();

// Streamlined navigation for demo focus
const navItems = [
  { path: '/', label: 'Operations', icon: LayoutDashboard, description: 'Real-time intelligence center' },
  { path: '/workflows', label: 'Workflows', icon: GitBranch, description: 'BUILD • HOST • GOVERN' },
  { path: '/orchestrator', label: 'Orchestrator', icon: Brain, description: 'Agent coordination' },
];

// Enhanced ElevateNow Header - Consistent with Agentic Studio
const ElevateNowHeaderWithNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  return (
    <header style={{
      background: 'var(--primary-bg)',
      color: 'var(--text-white)',
      height: '56px',
      display: 'flex',
      alignItems: 'center',
      padding: '0 20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      borderBottom: '1px solid var(--border-dark)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '32px', flex: 1 }}>
        {/* Brand Section */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-light) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '700',
            fontSize: '16px',
            color: 'white',
            boxShadow: '0 4px 8px rgba(74, 85, 104, 0.3)'
          }}>
            <Zap size={20} />
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: '700', letterSpacing: '-0.5px' }}>
              ElevateNow
            </div>
            <div style={{ fontSize: '11px', opacity: 0.7, color: 'var(--accent-light)' }}>
              Insurance Intelligence Platform
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '8px',
          marginLeft: '48px'
        }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
                           (item.path === '/orchestrator' && location.pathname.startsWith('/orchestrator/')) ||
                           (item.path === '/workflows' && location.pathname.startsWith('/workflows'));
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: isActive ? 'var(--accent-primary)' : 'transparent',
                  color: isActive ? 'white' : 'var(--text-white)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: isActive ? '600' : '500',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  position: 'relative',
                  opacity: isActive ? 1 : 0.8,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.opacity = '0.8';
                  }
                }}
                title={item.description}
              >
                <Icon size={18} />
                <span>{item.label}</span>
                {isActive && (
                  <div style={{
                    position: 'absolute',
                    bottom: '-2px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    background: 'var(--accent-light)',
                  }} />
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Right Side Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Platform Status Indicator */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          padding: '6px 12px',
          background: 'rgba(16, 185, 129, 0.15)',
          borderRadius: '20px',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }}>
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: 'var(--status-active)',
            animation: 'pulse 2s infinite'
          }} />
          <span style={{ 
            fontSize: '11px', 
            color: 'var(--status-active)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Live
          </span>
        </div>
        
        {/* Action Icons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button style={{
            width: '36px',
            height: '36px',
            border: 'none',
            background: 'transparent',
            color: 'var(--text-white)',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            opacity: 0.7,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.opacity = '0.7';
          }}
          >
            <Search size={18} />
          </button>
          
          <button style={{
            width: '36px',
            height: '36px',
            border: 'none',
            background: 'transparent',
            color: 'var(--text-white)',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            opacity: 0.7,
            position: 'relative',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.opacity = '0.7';
          }}
          >
            <Bell size={18} />
            <div style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: 'var(--status-warning)',
            }} />
          </button>
          
          {/* User Profile */}
          <div style={{ 
            width: '36px', 
            height: '36px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-blue) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
          >
            <User size={16} color="white" />
          </div>
        </div>
      </div>
    </header>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--divider-light)' }}>
          <ElevateNowHeaderWithNav />
          <main style={{ flex: 1, overflow: 'auto' }}>
            <Routes>
              <Route path="/" element={<EnhancedOperationsDashboard />} />
              <Route path="/workflows" element={<WorkflowStudioHome />} />
              <Route path="/workflows/:workflowId" element={<CompactWorkflowDetailView />} />
              <Route path="/orchestrator" element={<AgentOrchestrator />} />
              <Route path="/orchestrator/:agentId" element={<AgentOrchestrator />} />
            </Routes>
          </main>
        </div>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              boxShadow: 'var(--shadow-lg)',
            },
          }}
        />
      </Router>
    </QueryClientProvider>
  );
}

export default App;